/**
 * DiscoveryDossierForm.jsx
 * =========================
 * Formulaire dédié au "Dossier de mise en vente" — aligné sur le template
 * papier des agences (voir Dossier_mise_en_vente.docx).
 *
 * Affiché en sections repliables (Accordion) pour ne pas surcharger l'écran
 * mobile. Chaque champ est optionnel — l'agent remplit ce qui est pertinent
 * selon le bien (appart vs maison vs terrain).
 *
 * Props :
 *   - dossier : objet (form.property.dossier)
 *   - onChange : (path, value) => void  (path = clé du dossier, ex: 'pool_secured')
 *   - propertyType : 'apartment' | 'house' | 'villa' | 'mas' | 'village_house' | 'land' | ...
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/* eslint-disable react/no-unescaped-entities */

const Section = ({ title, hint, defaultOpen = false, children, testid }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="rounded-xl border border-white/10 bg-white/[0.02]" data-testid={testid}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left active:scale-[0.99] transition"
                data-testid={`${testid}-toggle`}
            >
                <div className="min-w-0">
                    <p className="text-sm font-bold text-foreground">{title}</p>
                    {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
                </div>
                {open ? <ChevronDown className="w-4 h-4 text-[#FF914D] flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
            </button>
            {open && (
                <div className="px-4 pb-4 pt-1 border-t border-white/5 space-y-3">
                    {children}
                </div>
            )}
        </div>
    );
};

const TextField = ({ label, value, onChange, placeholder, type = 'text', testid, cols = 1 }) => (
    <label className={`block ${cols === 2 ? 'sm:col-span-2' : ''}`}>
        <span className="block text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">{label}</span>
        <input
            type={type}
            value={value ?? ''}
            onChange={(e) => onChange(type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
            placeholder={placeholder || ''}
            className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-sm placeholder-white/20 focus:border-[#FF914D]/50 focus:outline-none"
            data-testid={testid}
        />
    </label>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
    <label className="block">
        <span className="block text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">{label}</span>
        <select
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value || null)}
            className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-sm focus:border-[#FF914D]/50 focus:outline-none"
            data-testid={testid}
        >
            <option value="">—</option>
            {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </select>
    </label>
);

const ToggleField = ({ label, value, onChange, testid }) => (
    <button
        type="button"
        onClick={() => onChange(!value)}
        data-testid={testid}
        className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm transition active:scale-[0.97] ${
            value ? 'bg-[#FF914D]/10 border-[#FF914D]/40 text-[#FF914D]' : 'bg-white/[0.02] border-white/10 text-muted-foreground'
        }`}
    >
        <span className="truncate text-left">{label}</span>
        <span className={`w-9 h-5 rounded-full relative transition flex-shrink-0 ${value ? 'bg-[#FF914D]' : 'bg-white/10'}`}>
            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${value ? 'left-4' : 'left-0.5'}`} />
        </span>
    </button>
);

export default function DiscoveryDossierForm({ dossier = {}, onChange, propertyType = 'apartment' }) {
    // Helper pour mettre à jour un champ
    const set = (key, value) => onChange(key, value);

    const d = dossier || {};

    const isHouse = ['house', 'villa', 'mas', 'village_house'].includes(propertyType);
    const isApartment = propertyType === 'apartment';
    const isLand = propertyType === 'land';

    return (
        <div className="space-y-2">
            <p className="text-[11px] text-muted-foreground italic">
                Toutes les sections sont optionnelles — remplissez ce qui est pertinent pour ce bien.
                Vous pourrez exporter la fiche complète en PDF pour votre CRM.
            </p>

            {/* Caractéristiques principales */}
            <Section
                title="Caractéristiques principales"
                hint="État général, environnement, mitoyenneté, construction"
                defaultOpen
                testid="dossier-section-general"
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <TextField label="Référence cadastrale" value={d.referenced_cadastrale} onChange={(v) => set('referenced_cadastrale', v)} placeholder="Section, n°, superficie" testid="dossier-ref-cadastrale" cols={2} />
                    <SelectField label="État général" value={d.etat_general} onChange={(v) => set('etat_general', v)} options={[
                        { value: 'neuf', label: 'Neuf' },
                        { value: 'bon', label: 'Bon état' },
                        { value: 'a_rafraichir', label: 'À rafraîchir' },
                        { value: 'a_renover', label: 'À rénover' },
                    ]} testid="dossier-etat-general" />
                    <SelectField label="Mitoyenneté" value={d.mitoyennete} onChange={(v) => set('mitoyennete', v)} options={[
                        { value: 'individuel', label: 'Individuel' },
                        { value: 'mitoyen', label: 'Mitoyen' },
                        { value: 'semi_mitoyen', label: 'Semi-mitoyen' },
                    ]} testid="dossier-mitoyennete" />
                    <TextField label="Situation / environnement" value={d.situation_environnement} onChange={(v) => set('situation_environnement', v)} placeholder="Quartier, voisinage…" testid="dossier-situation" cols={2} />
                    <TextField label="Type de construction" value={d.type_construction} onChange={(v) => set('type_construction', v)} placeholder="Pierre, parpaing, ossature bois…" testid="dossier-type-construction" />
                    <TextField label="Conseiller (vous)" value={d.conseiller_nom} onChange={(v) => set('conseiller_nom', v)} placeholder="Nom du conseiller" testid="dossier-conseiller" />
                </div>
            </Section>

            {/* Chauffage / Climatisation */}
            <Section
                title="Chauffage, climatisation & confort thermique"
                hint="Type, année, cheminée, insert, poêle…"
                testid="dossier-section-heating"
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <TextField label="Année du chauffage" value={d.heating_year} onChange={(v) => set('heating_year', v)} type="number" placeholder="Ex: 2015" testid="dossier-heating-year" />
                    <TextField label="Isolation toiture" value={d.isolation_toiture} onChange={(v) => set('isolation_toiture', v)} placeholder="Oui / Non / Type" testid="dossier-isolation" />
                </div>
                <div className="grid grid-cols-2 gap-2 mt-1">
                    <ToggleField label="Climatisation" value={d.air_conditioning} onChange={(v) => set('air_conditioning', v)} testid="dossier-clim" />
                    <ToggleField label="Cheminée" value={d.cheminee} onChange={(v) => set('cheminee', v)} testid="dossier-cheminee" />
                    <ToggleField label="Insert" value={d.insert} onChange={(v) => set('insert', v)} testid="dossier-insert" />
                    <ToggleField label="Poêle à bois" value={d.poele_bois} onChange={(v) => set('poele_bois', v)} testid="dossier-poele" />
                </div>
            </Section>

            {/* Piscine */}
            {!isLand && (
                <Section
                    title="Piscine & extérieurs"
                    hint="Piscine, pool house, jardin, vis-à-vis…"
                    testid="dossier-section-pool"
                >
                    <div className="grid grid-cols-2 gap-2 mb-2">
                        <ToggleField label="Piscine" value={d.pool} onChange={(v) => set('pool', v)} testid="dossier-pool" />
                        <ToggleField label="Pool house" value={d.pool_house} onChange={(v) => set('pool_house', v)} testid="dossier-pool-house" />
                        <ToggleField label="Piscine sécurisée" value={d.pool_secured} onChange={(v) => set('pool_secured', v)} testid="dossier-pool-secured" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <TextField label="Dimension piscine" value={d.pool_dimension} onChange={(v) => set('pool_dimension', v)} placeholder="Ex: 8m x 4m" testid="dossier-pool-dim" />
                        <TextField label="Détail pool house" value={d.pool_house_details} onChange={(v) => set('pool_house_details', v)} placeholder="Surface, équipement" testid="dossier-pool-house-detail" />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                        <ToggleField label="Jardin arboré" value={d.jardin_arbore} onChange={(v) => set('jardin_arbore', v)} testid="dossier-jardin-arbore" />
                        <ToggleField label="Jardin clôturé" value={d.jardin_cloture} onChange={(v) => set('jardin_cloture', v)} testid="dossier-jardin-cloture" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                        <TextField label="Vis-à-vis" value={d.vis_a_vis} onChange={(v) => set('vis_a_vis', v)} placeholder="Oui / Non / description" testid="dossier-vis-a-vis" />
                        <TextField label="Nuisances sonores" value={d.nuisances_sonores} onChange={(v) => set('nuisances_sonores', v)} placeholder="Aucune / type" testid="dossier-nuisances" />
                    </div>
                </Section>
            )}

            {/* Structure & pièces */}
            <Section
                title="Étages, pièces & dépendances"
                testid="dossier-section-structure"
            >
                <div className="grid grid-cols-3 gap-3">
                    <TextField label="Nb étages" value={d.nbre_etages} onChange={(v) => set('nbre_etages', v)} type="number" testid="dossier-nb-etages" />
                    <TextField label="Nb chambres" value={d.nbre_chambres} onChange={(v) => set('nbre_chambres', v)} type="number" testid="dossier-nb-chambres" />
                    <TextField label="Nb dépendances" value={d.nbre_dependances} onChange={(v) => set('nbre_dependances', v)} type="number" testid="dossier-nb-dependances" />
                </div>
            </Section>

            {/* Réseaux */}
            <Section
                title="Réseaux & viabilités"
                hint="Eau, EDF, gaz, fosse septique, VMC…"
                testid="dossier-section-networks"
            >
                <div className="grid grid-cols-2 gap-2">
                    <ToggleField label="Eau de ville" value={d.eau_ville} onChange={(v) => set('eau_ville', v)} testid="dossier-eau-ville" />
                    <ToggleField label="EDF raccordé" value={d.edf} onChange={(v) => set('edf', v)} testid="dossier-edf" />
                    <ToggleField label="Gaz de ville" value={d.gaz_ville} onChange={(v) => set('gaz_ville', v)} testid="dossier-gaz-ville" />
                    <ToggleField label="Gaz propane" value={d.gaz_propane} onChange={(v) => set('gaz_propane', v)} testid="dossier-gaz-propane" />
                    <ToggleField label="VMC" value={d.vmc} onChange={(v) => set('vmc', v)} testid="dossier-vmc" />
                    <ToggleField label="Tout à l'égout" value={d.tae} onChange={(v) => set('tae', v)} testid="dossier-tae" />
                    <ToggleField label="Fosse septique" value={d.fosse_septique} onChange={(v) => set('fosse_septique', v)} testid="dossier-fosse" />
                </div>
                <div className="grid grid-cols-1 gap-3 mt-3">
                    <TextField label="Forage / canal / puits" value={d.forage_canal_puit} onChange={(v) => set('forage_canal_puit', v)} placeholder="Détails si applicable" testid="dossier-forage" />
                    <TextField label="Travaux des 10 dernières années" value={d.travaux_10ans} onChange={(v) => set('travaux_10ans', v)} placeholder="Description, dates, montants" testid="dossier-travaux-10ans" cols={2} />
                </div>
            </Section>

            {/* Sécurité & confort */}
            <Section
                title="Sécurité & confort"
                hint="Interphone, portail, volets, alarme…"
                testid="dossier-section-security"
            >
                <div className="grid grid-cols-2 gap-2">
                    <ToggleField label="Interphone" value={d.interphone} onChange={(v) => set('interphone', v)} testid="dossier-interphone" />
                    <ToggleField label="Portail électrique" value={d.portail_electrique} onChange={(v) => set('portail_electrique', v)} testid="dossier-portail" />
                    <ToggleField label="Volets électriques" value={d.volets_electriques} onChange={(v) => set('volets_electriques', v)} testid="dossier-volets-elec" />
                    <ToggleField label="Volets bois" value={d.volets_bois} onChange={(v) => set('volets_bois', v)} testid="dossier-volets-bois" />
                    <ToggleField label="Moustiquaires" value={d.moustiquaires} onChange={(v) => set('moustiquaires', v)} testid="dossier-moustiquaires" />
                    <ToggleField label="Ascenseur" value={d.ascenseur} onChange={(v) => set('ascenseur', v)} testid="dossier-ascenseur" />
                    <ToggleField label="Alarme" value={d.alarme} onChange={(v) => set('alarme', v)} testid="dossier-alarme" />
                    <ToggleField label="Vide sanitaire" value={d.vide_sanitaire} onChange={(v) => set('vide_sanitaire', v)} testid="dossier-vide-sanitaire" />
                </div>
            </Section>

            {/* Terrain (si maison/villa/terrain) */}
            {(isHouse || isLand) && (
                <Section
                    title="Terrain — urbanisme & viabilités"
                    hint="PLU, aspect, lotissement, autorisations…"
                    testid="dossier-section-land"
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <TextField label="Zone PLU" value={d.zone_plu} onChange={(v) => set('zone_plu', v)} placeholder="Ex: UA, UB…" testid="dossier-plu" />
                        <SelectField label="Aspect" value={d.terrain_aspect} onChange={(v) => set('terrain_aspect', v)} options={[
                            { value: 'plat', label: 'Plat' },
                            { value: 'pentu', label: 'Pentu' },
                            { value: 'restanques', label: 'Restanques' },
                        ]} testid="dossier-aspect" />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                        <ToggleField label="Lotissement" value={d.lotissement} onChange={(v) => set('lotissement', v)} testid="dossier-lotissement" />
                        <ToggleField label="Bornage" value={d.bornage} onChange={(v) => set('bornage', v)} testid="dossier-bornage" />
                        <ToggleField label="Zone inondable" value={d.zone_inondable} onChange={(v) => set('zone_inondable', v)} testid="dossier-inondable" />
                        <ToggleField label="Document arpentage" value={d.document_arpentage} onChange={(v) => set('document_arpentage', v)} testid="dossier-arpentage" />
                        <ToggleField label="Divisible" value={d.divisible} onChange={(v) => set('divisible', v)} testid="dossier-divisible" />
                        <ToggleField label="Servitude de passage" value={d.servitude_passage} onChange={(v) => set('servitude_passage', v)} testid="dossier-servitude" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                        <TextField label="Géomètre (nom)" value={d.geometre_nom} onChange={(v) => set('geometre_nom', v)} testid="dossier-geometre" />
                        <SelectField label="Autorisation administrative" value={d.autorisation_administrative} onChange={(v) => set('autorisation_administrative', v)} options={[
                            { value: 'dp', label: 'DP' },
                            { value: 'pa', label: 'Permis d\'aménager' },
                            { value: 'aucune', label: 'Aucune' },
                        ]} testid="dossier-autorisation" />
                        <TextField label="Viabilités (raccordements)" value={d.viabilites} onChange={(v) => set('viabilites', v)} placeholder="Égout, eau, EDF, GDF, téléphone, canal…" testid="dossier-viabilites" cols={2} />
                    </div>
                </Section>
            )}

            {/* Copropriété (si appartement) */}
            {isApartment && (
                <Section
                    title="Copropriété & immeuble"
                    hint="Charges, syndic, lots, équipements"
                    testid="dossier-section-copro"
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <SelectField label="Chauffage" value={d.copro_chauffage_type} onChange={(v) => set('copro_chauffage_type', v)} options={[
                            { value: 'individuel', label: 'Individuel' },
                            { value: 'collectif', label: 'Collectif' },
                        ]} testid="dossier-copro-chauffage" />
                        <TextField label="Charges mensuelles (€)" value={d.copro_charges_mensuelles} onChange={(v) => set('copro_charges_mensuelles', v)} type="number" testid="dossier-charges" />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                        <ToggleField label="Appartement loué" value={d.copro_appartement_loue} onChange={(v) => set('copro_appartement_loue', v)} testid="dossier-loue" />
                    </div>
                    {d.copro_appartement_loue && (
                        <TextField label="Loyer mensuel (€)" value={d.copro_loyer_mensuel} onChange={(v) => set('copro_loyer_mensuel', v)} type="number" testid="dossier-loyer" />
                    )}
                    <div className="grid grid-cols-1 gap-3 mt-3">
                        <TextField label="N° des lots et tantièmes" value={d.copro_lots_tantiemes} onChange={(v) => set('copro_lots_tantiemes', v)} testid="dossier-lots" />
                        <TextField label="Syndic (nom, adresse, tél, mail)" value={d.copro_syndic} onChange={(v) => set('copro_syndic', v)} testid="dossier-syndic" cols={2} />
                        <TextField label="Équipement immeuble" value={d.copro_equipement_immeuble} onChange={(v) => set('copro_equipement_immeuble', v)} placeholder="Ascenseur, digicode, gardien, piscine…" testid="dossier-equip-immeuble" cols={2} />
                        <TextField label="Travaux votés (description, montant)" value={d.copro_travaux_votes} onChange={(v) => set('copro_travaux_votes', v)} testid="dossier-travaux-votes" cols={2} />
                    </div>
                </Section>
            )}

            {/* Diagnostics */}
            <Section
                title="Diagnostics à réaliser"
                hint="Cochez ce qui est requis pour ce bien"
                testid="dossier-section-diag"
            >
                <div className="grid grid-cols-2 gap-2">
                    <ToggleField label="Thermites / Mérules" value={d.diag_thermites_merules} onChange={(v) => set('diag_thermites_merules', v)} testid="diag-thermites" />
                    <ToggleField label="ERNT (risques)" value={d.diag_ernt} onChange={(v) => set('diag_ernt', v)} testid="diag-ernt" />
                    <ToggleField label="Assainissement" value={d.diag_assainissement} onChange={(v) => set('diag_assainissement', v)} testid="diag-assainissement" />
                    <ToggleField label="Loi Carrez" value={d.diag_carrez} onChange={(v) => set('diag_carrez', v)} testid="diag-carrez" />
                    <ToggleField label="Amiante" value={d.diag_amiante} onChange={(v) => set('diag_amiante', v)} testid="diag-amiante" />
                    <ToggleField label="Plomb" value={d.diag_plomb} onChange={(v) => set('diag_plomb', v)} testid="diag-plomb" />
                    <ToggleField label="Gaz (+15 ans)" value={d.diag_gaz} onChange={(v) => set('diag_gaz', v)} testid="diag-gaz" />
                    <ToggleField label="Électricité" value={d.diag_electricite} onChange={(v) => set('diag_electricite', v)} testid="diag-electricite" />
                    <ToggleField label="Détecteur fumée" value={d.diag_detecteur_fumee} onChange={(v) => set('diag_detecteur_fumee', v)} testid="diag-fumee" />
                    {(d.pool || !isLand) && (
                        <ToggleField label="Sécurité piscine" value={d.diag_securite_piscine} onChange={(v) => set('diag_securite_piscine', v)} testid="diag-piscine" />
                    )}
                    {isLand && (
                        <ToggleField label="Étude de sol" value={d.diag_etude_sol} onChange={(v) => set('diag_etude_sol', v)} testid="diag-sol" />
                    )}
                    {isApartment && (
                        <ToggleField label="DTG (diag global)" value={d.diag_dtg} onChange={(v) => set('diag_dtg', v)} testid="diag-dtg" />
                    )}
                </div>
            </Section>

            {/* Impôts & disponibilité */}
            <Section
                title="Impôts, disponibilité & notaire"
                testid="dossier-section-tax"
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <TextField label="Taxe foncière (€/an)" value={d.taxe_fonciere} onChange={(v) => set('taxe_fonciere', v)} type="number" testid="dossier-taxe-fonciere" />
                    <TextField label="Taxe habitation (€/an)" value={d.taxe_habitation} onChange={(v) => set('taxe_habitation', v)} type="number" testid="dossier-taxe-habitation" />
                    <TextField label="Date de disponibilité" value={d.date_disponibilite} onChange={(v) => set('date_disponibilite', v)} type="date" testid="dossier-date-dispo" />
                    <TextField label="Date du bail (si loué)" value={d.date_bail} onChange={(v) => set('date_bail', v)} type="date" testid="dossier-date-bail" />
                    <TextField label="Notaire (coordonnées)" value={d.notaire_coordonnees} onChange={(v) => set('notaire_coordonnees', v)} placeholder="Nom, tél, mail" testid="dossier-notaire" cols={2} />
                    <TextField label="Consignes de visite" value={d.consignes_visite} onChange={(v) => set('consignes_visite', v)} placeholder="Horaires, code, contact…" testid="dossier-consignes" cols={2} />
                </div>
            </Section>

            {/* Vendeur juridique */}
            <Section
                title="Vendeur — Aspect juridique"
                hint="Régime matrimonial, indivision, succession"
                testid="dossier-section-legal"
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <TextField label="Date de mariage" value={d.mariage_date} onChange={(v) => set('mariage_date', v)} type="date" testid="dossier-mariage" />
                    <SelectField label="Régime matrimonial" value={d.regime_matrimonial} onChange={(v) => set('regime_matrimonial', v)} options={[
                        { value: 'communaute_reduite', label: 'Communauté réduite aux acquêts' },
                        { value: 'separation_biens', label: 'Séparation de biens' },
                        { value: 'communaute_universelle', label: 'Communauté universelle' },
                        { value: 'participation_acquets', label: 'Participation aux acquêts' },
                        { value: 'pacs', label: 'PACS' },
                        { value: 'celibataire', label: 'Célibataire' },
                    ]} testid="dossier-regime" />
                    <TextField label="Nb indivisaires" value={d.nbre_indivisaires} onChange={(v) => set('nbre_indivisaires', v)} type="number" testid="dossier-indivisaires" />
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                    <ToggleField label="Succession" value={d.succession} onChange={(v) => set('succession', v)} testid="dossier-succession" />
                </div>
            </Section>

            {/* Vente */}
            <Section
                title="Motivation & prix souhaité"
                defaultOpen
                testid="dossier-section-sale"
            >
                <div className="grid grid-cols-1 gap-3">
                    <TextField label="Raison de la vente" value={d.raison_vente} onChange={(v) => set('raison_vente', v)} placeholder="Mutation, agrandissement, héritage…" testid="dossier-raison" cols={2} />
                    <TextField label="Prix souhaité par le vendeur (€)" value={d.estimation_prix_souhaitee} onChange={(v) => set('estimation_prix_souhaitee', v)} type="number" testid="dossier-prix-souhaite" />
                </div>
            </Section>
        </div>
    );
}
